import { z } from 'zod'
import { SectionPermissions } from './deling.js'
import { Block, DossierId, InstantieId, ToekenningId } from './id.js'
import { Side, Role } from './identity.js'
import { Moment, Person, compareMoments } from './moment.js'

/**
 * Het register van toekenningen: wie waarbij mag (R-155b, ontwerp 4.8, B18).
 *
 * Een toekenning noemt de persoon, het dossier, de bouwblokken, de rol, de kant en de bron.
 * Er zijn drie bronnen. Het **processysteem** zet per dossier de volledige lijst van zijn
 * eigen toekenningen, en het register wordt daaraan gelijkgezet (R-155d). Een **deling** in
 * het formulier schrijft een toekenning met het recht per sectie, de deler en de instantie
 * waarop is gedeeld. En **beheer** schrijft een toekenning bij een opening door functioneel
 * beheer, met reden, meldingsnummer en einde (R-155f).
 *
 * **Eén platte vorm met velden per bron, en geen unie van drie vormen.** Zo heeft de tabel
 * `grant` één kolom per veld en bindt `schema.test.ts` haar aan deze vorm zoals elke andere
 * tabel. De prijs is dat de vorm zelf niet zegt welk veld bij welke bron hoort; dat doet de
 * `superRefine` hieronder: de velden van de ene bron staan nooit op een andere, en een bron
 * mist haar eigen velden niet. Een veld dat niet bij de bron hoort is `null` en niet
 * weggelaten, net als bij de deling, zodat "niet van toepassing" en "vergeten" niet op
 * hetzelfde neerkomen.
 *
 * Wat deze vorm niet doet: uitmaken wat iemand op een sectie mag. Dat rekent de pod uit de
 * toekenningen, de deling en de beurt (5.2, R-65a); hier staat alleen wat er is toegekend.
 */

/** Waar een toekenning vandaan komt (R-155b). */
export const ToekenningSource = z.enum(['processysteem', 'deling', 'beheer'])
export type ToekenningSource = z.infer<typeof ToekenningSource>

/**
 * De toestand van een toekenning uit een deling (R-64c, R-66, R-65e).
 *
 * Uitgenodigd geeft nog geen toegang, binnengekomen wel. Geweigerd, ingetrokken en vervallen
 * zijn eindtoestanden: er gaat geen weg terug, en dat is voor vervallen de kern van R-65e,
 * want een vervallen deling leeft niet weer op als de deler later opnieuw wordt toegekend.
 */
export const DelingStatus = z.enum(['uitgenodigd', 'binnengekomen', 'geweigerd', 'ingetrokken', 'vervallen'])
export type DelingStatus = z.infer<typeof DelingStatus>

/**
 * De bouwblokken waarvoor een toekenning geldt (R-155c).
 *
 * Verplicht en niet leeg, en er is geen waarde die alle bouwblokken betekent: een blok is een
 * slug, dus `*` past er niet in. Onder één dossier hangen de aanvraag en de beoordeling, en
 * een toekenning per dossier alleen zou de aanvrager de beoordeling laten openen.
 */
export const Blocks = z
  .array(Block)
  .min(1, 'een toekenning geldt voor minstens één bouwblok; er is geen waarde die alle bouwblokken betekent (R-155c)')
  .refine((blocks) => new Set(blocks).size === blocks.length, {
    message: 'een bouwblok staat hoogstens één keer in de lijst',
  })
export type Blocks = z.infer<typeof Blocks>

/** Een vrije tekst die er moet zijn als hij er staat: een reden, een toelichting, een nummer. */
const Filled = z.string().trim().min(1)

/** Wat elke toekenning aan regels deelt, waar ze ook vandaan komt. */
interface Core {
  readonly persoon: string
  readonly rol: Role
  readonly kant: Side
  readonly namens?: string | null | undefined
}

/**
 * De regels die voor een toekenning gelden en voor een element van de lijst van het
 * processysteem: één functie, zodat ze op twee plekken hetzelfde zeggen.
 *
 * Een toetser staat aan de interne kant (R-155a). Dit is het tweede slot: de koppelvlakdienst
 * leidt de kant af uit de ingang van de persoon, en het register weigert een externe toetser
 * daarnaast zelf, zodat een fout in die afleiding hier stopt en niet in de toetslaag.
 *
 * Niemand handelt namens zichzelf (R-190): `namens` gelijk aan `persoon` zou een gemachtigde
 * zonder volmachtgever zijn, en in de revisie (R-191) een indiening namens niemand anders.
 */
function checkCore(core: Core, ctx: z.RefinementCtx): void {
  if (core.rol === 'toetser' && core.kant !== 'intern') {
    ctx.addIssue({
      code: 'custom',
      path: ['kant'],
      message: 'een toetser staat aan de interne kant; een toekenning als toetser voor de externe kant bestaat niet (R-155a)',
    })
  }
  if (core.namens !== null && core.namens !== undefined && core.namens === core.persoon) {
    ctx.addIssue({
      code: 'custom',
      path: ['namens'],
      message: 'niemand handelt namens zichzelf (R-190)',
    })
  }
}

const DELING_FIELDS = ['deler', 'rechten', 'instantieId', 'toestand'] as const
const ADMIN_FIELDS = ['einde', 'redencategorie', 'toelichting', 'meldingsnummer'] as const

/** Welke velden bij welke bron horen. `namens` hoort bij het processysteem en is daar optioneel. */
const OWN_FIELDS: Readonly<Record<ToekenningSource, readonly string[]>> = {
  processysteem: [],
  deling: DELING_FIELDS,
  beheer: ADMIN_FIELDS,
}

export const Toekenning = z
  .strictObject({
    id: ToekenningId,
    /** Het dossier waarvoor de toekenning geldt; één toekenning, één dossier (R-155c). */
    dossierId: DossierId,
    /** De ingang met de vaste sleutel van de persoon (R-155e). */
    persoon: Person,
    blokken: Blocks,
    /** Welke laag iemand schrijft (R-172b). Er zijn er precies twee (D-23). */
    rol: Role,
    /**
     * Intern of extern. Volgt uit de ingang en nooit uit de invoer of uit een aanroep van het
     * processysteem (R-155a); wie hem hier neerzet is de koppelvlakdienst, die hem uit de
     * ingang afleidt, of de pod, die hem uit het token heeft.
     */
    kant: Side,
    bron: ToekenningSource,
    /** Namens wie een gemachtigde handelt (R-190). Alleen bij bron processysteem, en dan optioneel. */
    namens: Person.nullable(),
    /** Bij een deling: wie deelde (R-155b). De cascade van R-65e loopt hierop. */
    deler: Person.nullable(),
    /** Bij een deling: het recht per sectie (R-67). */
    rechten: SectionPermissions.nullable(),
    /** Bij een deling: de instantie waarop is gedeeld. Haar bouwblok is het ene blok van de toekenning (R-155b). */
    instantieId: InstantieId.nullable(),
    /** Bij een deling: waar de uitnodiging staat (R-64c). */
    toestand: DelingStatus.nullable(),
    /** Bij beheer: tot wanneer de opening geldt (R-155f). */
    einde: Moment.nullable(),
    /** Bij beheer: de categorie van de reden, die de aanvrager te zien krijgt (R-66b). */
    redencategorie: Filled.nullable(),
    /** Bij beheer: de interne toelichting op de reden (R-155f). */
    toelichting: Filled.nullable(),
    /** Bij beheer: het nummer van de melding waarvoor werd geopend (R-155f, R-155g). */
    meldingsnummer: Filled.nullable(),
    aangemaaktOp: Moment,
    /**
     * Wanneer de toekenning verviel. Bij het processysteem: het moment waarop ze uit zijn lijst
     * verdween (R-155d). Bij een deling: het moment waarop de toegang van de deler verviel
     * (R-65e). De rij blijft staan voor de herleidbaarheid.
     */
    vervallenOp: Moment.nullable(),
    /** Bij een deling: wanneer de deler haar introk (R-66). */
    ingetrokkenOp: Moment.nullable(),
  })
  .superRefine((toekenning, ctx) => {
    checkCore(toekenning, ctx)

    // De velden per bron: eigen velden gevuld, andermans velden leeg.
    for (const [source, fields] of Object.entries(OWN_FIELDS)) {
      const own = source === toekenning.bron
      for (const field of fields) {
        const empty = toekenning[field as keyof Toekenning] === null
        if (own && empty) {
          ctx.addIssue({ code: 'custom', path: [field], message: `een toekenning met bron ${source} draagt ${field}` })
        }
        if (!own && !empty) {
          ctx.addIssue({
            code: 'custom',
            path: [field],
            message: `${field} hoort bij bron ${source} en niet bij bron ${toekenning.bron}`,
          })
        }
      }
    }
    if (toekenning.bron !== 'processysteem' && toekenning.namens !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['namens'],
        message: `namens komt van het processysteem (R-190) en hoort niet bij bron ${toekenning.bron}`,
      })
    }

    if (toekenning.bron === 'deling') {
      if (toekenning.blokken.length !== 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['blokken'],
          message: 'een deling geldt voor precies één bouwblok: dat van de instantie waarop is gedeeld (R-155b)',
        })
      }
      // Het einde draagt zijn moment, en alleen dat einde.
      if ((toekenning.toestand === 'vervallen') !== (toekenning.vervallenOp !== null)) {
        ctx.addIssue({
          code: 'custom',
          path: ['vervallenOp'],
          message: 'een vervallen deling draagt het moment waarop ze verviel, en alleen die',
        })
      }
      if ((toekenning.toestand === 'ingetrokken') !== (toekenning.ingetrokkenOp !== null)) {
        ctx.addIssue({
          code: 'custom',
          path: ['ingetrokkenOp'],
          message: 'een ingetrokken deling draagt het moment waarop ze werd ingetrokken, en alleen die',
        })
      }
    } else if (toekenning.ingetrokkenOp !== null) {
      // Het processysteem trekt in door een toekenning uit zijn lijst te halen, en dan vervalt
      // ze (R-155d). Beheer eindigt met zijn einde.
      ctx.addIssue({
        code: 'custom',
        path: ['ingetrokkenOp'],
        message: `een toekenning met bron ${toekenning.bron} wordt niet ingetrokken`,
      })
    }

    if (toekenning.bron === 'beheer') {
      // Functioneel beheer wordt alleen op de interne ingang aanvaard (R-155f, R-155a).
      if (toekenning.kant !== 'intern') {
        ctx.addIssue({
          code: 'custom',
          path: ['kant'],
          message: 'een opening door beheer staat aan de interne kant (R-155f)',
        })
      }
      if (toekenning.vervallenOp !== null) {
        ctx.addIssue({
          code: 'custom',
          path: ['vervallenOp'],
          message: 'een opening door beheer eindigt met haar einde en vervalt niet apart (R-155f)',
        })
      }
      if (toekenning.einde !== null && compareMoments(toekenning.einde, toekenning.aangemaaktOp) <= 0) {
        ctx.addIssue({ code: 'custom', path: ['einde'], message: 'een opening door beheer eindigt na haar begin' })
      }
    }

    for (const field of ['vervallenOp', 'ingetrokkenOp'] as const) {
      const moment = toekenning[field]
      if (moment !== null && compareMoments(moment, toekenning.aangemaaktOp) < 0) {
        ctx.addIssue({ code: 'custom', path: [field], message: 'een toekenning eindigt niet voordat ze bestond' })
      }
    }
  })
export type Toekenning = z.infer<typeof Toekenning>

/**
 * Eén element van de lijst die het processysteem per dossier zet (R-155d).
 *
 * De velden komen uit `Toekenning` en de regels uit dezelfde functie, dus wat hier past, past
 * daar ook. De kant staat erin omdat de opslagdienst hem van de koppelvlakdienst krijgt, die
 * hem uit de ingang afleidt; het processysteem zelf geeft hem niet door (R-155a,
 * `docs/koppelvlak.md` hoofdstuk 6).
 */
export const ToekenningFromProcessysteem = z
  .strictObject({
    persoon: Toekenning.shape.persoon,
    rol: Toekenning.shape.rol,
    kant: Toekenning.shape.kant,
    blokken: Toekenning.shape.blokken,
    namens: Person.optional(),
  })
  .superRefine(checkCore)
export type ToekenningFromProcessysteem = z.infer<typeof ToekenningFromProcessysteem>

/**
 * De volledige lijst van het processysteem op één dossier (R-155d).
 *
 * Leeg mag: dan kent het processysteem op dat dossier niemand meer iets toe. Een element wordt
 * aangewezen door persoon en rol, want daaraan wordt het register gelijkgezet; dezelfde
 * persoon met dezelfde rol twee keer is een fout en geen stille samenvoeging, want welke van
 * de twee lijsten bouwblokken zou dan gelden?
 */
export const ProcessysteemList = z
  .strictObject({ toekenningen: z.array(ToekenningFromProcessysteem) })
  .superRefine((list, ctx) => {
    const seen = new Map<string, number>()
    list.toekenningen.forEach((element, index) => {
      const key = `${element.persoon} ${element.rol}`
      const earlier = seen.get(key)
      if (earlier === undefined) {
        seen.set(key, index)
        return
      }
      ctx.addIssue({
        code: 'custom',
        path: ['toekenningen', index],
        message: `${element.persoon} staat al met rol ${element.rol} op plaats ${earlier}; één toekenning per persoon en rol`,
      })
    })
  })
export type ProcessysteemList = z.infer<typeof ProcessysteemList>
